import { useState } from 'react';
import { cartService } from '@/features/cart/services/cartService';
import type { CartItemResponse, UpdateCartItemRequestBody, CartItemStatus, AddOnSelection } from '@/features/cart/types';
import type { SelectedAddOn } from '@/features/cart/types';

export interface UseCartItemOperationsReturn {
  isUpdating: boolean;
  isRemoving: boolean;
  updateCartItem: (
    cartItem: CartItemResponse,
    editGuestCount: number,
    editAddOnsPerGuest: SelectedAddOn[][],
    removedGuests: Set<number>,
    removedItems: Set<string>,
    onSuccess: () => void
  ) => Promise<void>;
  confirmPrice: (
    cartItemId: string,
    onSuccess: () => void,
    onStatusUpdate?: (status: CartItemStatus) => void
  ) => Promise<void>;
  removeItem: (cartItemId: string, onSuccess: () => void) => Promise<void>;
}

export function useCartItemOperations(): UseCartItemOperationsReturn {
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const updateCartItem = async (
    cartItem: CartItemResponse,
    editGuestCount: number,
    editAddOnsPerGuest: SelectedAddOn[][],
    removedGuests: Set<number>,
    removedItems: Set<string>,
    onSuccess: () => void
  ) => {
    // Filter out removed guests and removed items from editAddOnsPerGuest
    // Transform to AddOnSelection format with quantities
    const addOnSelectionsPerGuest: AddOnSelection[][] = [];

    editAddOnsPerGuest.forEach((guestAddOns, guestIndex) => {
      // Skip guests that were completely removed
      if (removedGuests.has(guestIndex)) {
        return;
      }

      // For remaining guests, filter out removed add-ons and keep quantity
      const filteredSelections: AddOnSelection[] = guestAddOns
        .filter(addOn => {
          const key = `${guestIndex}-${addOn.add_on_id}`;
          return !removedItems.has(key);
        })
        .map(addOn => ({
          add_on_id: addOn.add_on_id,
          quantity: addOn.quantity || 1
        }));

      addOnSelectionsPerGuest.push(filteredSelections);
    });

    const updateBody: UpdateCartItemRequestBody = {
      total_guests: editGuestCount,
      add_on_selections_per_guest: addOnSelectionsPerGuest
    };

    setIsUpdating(true);
    try {
      await cartService.updateCartItem(cartItem.id, updateBody);
      onSuccess();
    } catch (error) {
      console.error('Error updating cart item:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const confirmPrice = async (
    cartItemId: string,
    onSuccess: () => void,
    onStatusUpdate?: (status: CartItemStatus) => void
  ) => {
    setIsUpdating(true);
    try {
      // Step 1: Confirm the price
      await cartService.confirmCartItemPrice(cartItemId);

      // Step 2: Fetch the status of the cart item
      const statusResponse = await cartService.getCartItemStatus(cartItemId);

      // Step 3: Update the status in parent component if callback provided
      if (onStatusUpdate && statusResponse.status) {
        onStatusUpdate(statusResponse.status);
      }

      onSuccess();
    } catch (error) {
      console.error('Error confirming price:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  const removeItem = async (cartItemId: string, onSuccess: () => void) => {
    setIsRemoving(true);
    try {
      await cartService.removeCartItem(cartItemId);
      onSuccess();
    } catch (error) {
      console.error('Error removing cart item:', error);
    } finally {
      setIsRemoving(false);
    }
  };

  return {
    isUpdating,
    isRemoving,
    updateCartItem,
    confirmPrice,
    removeItem,
  };
}

